import React, { useEffect } from 'react';
import { type Control, useController, useWatch } from 'react-hook-form';

import DatePicker from '@components/form/DatePicker/DatePicker';
import TextArea from '@components/form/TextArea/TextArea';
import Checkbox from '@components/uikit/Checkbox/Checkbox';
import Input from '@components/uikit/Input/Input';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { ENVIRONMENT, environments } from '../../constants/environmentsOptions';
import { type IWarrantyForm, WARRANTY_FIELDS } from '../../constants/schemaWarranty';
import style from './style.module.scss';

interface IProps {
  control: Control<IWarrantyForm>;
}

const StepDeviceInformation: React.FC<IProps> = ({ control }) => {
  const { t } = useTranslation();

  const { data: profileData } = useUserProfileQuery();

  const whatEnvironmentsFieldValue = useWatch({ name: WARRANTY_FIELDS.WHAT_ENVIRONMENT, control });

  const {
    field: { onChange: otherEnvironmentFieldOnChange },
  } = useController({ name: WARRANTY_FIELDS.OTHER_ENVIRONMENT, control });

  useEffect(() => {
    if (!whatEnvironmentsFieldValue[ENVIRONMENT.OTHER]) otherEnvironmentFieldOnChange('');
  }, [whatEnvironmentsFieldValue[ENVIRONMENT.OTHER]]);

  return (
    <div className={style.stepDeviceInformationContainer}>
      <div className={style.stepDeviceInformationInputsContainer}>
        <Input
          control={control}
          name={WARRANTY_FIELDS.PRODUCT_TYPE}
          label={t('Product Name or Number')}
          tooltipContent={t('Enter the part number or the general name of the device being submitted.')}
          tooltipPlace="bottom"
        />
        <TextArea
          control={control}
          name={WARRANTY_FIELDS.DEVICE_ID_NUMBER}
          label={t('Device ID Numbers')}
          tooltipContent={t(
            'Enter the ID printed on the device enclosure or label. Separate multiple devices with commas.',
          )}
          tooltipPlace="bottom-end"
          required
        />
        <div className={style.stepDeviceAdditionalInformationInputsContainer}>
          <Input
            control={control}
            name={WARRANTY_FIELDS.EQUIPMENT_LOCATION}
            label={t('Equipment Location')}
            tooltipContent={t('Enter the name of the equipment this device was installed on, if known.')}
            tooltipPlace="bottom"
            type="woHelperText"
          />
          <DatePicker
            control={control}
            name={WARRANTY_FIELDS.INSTALL_DATE}
            label={t('Install Date (if known)')}
            dateFormat={`${profileData?.dateFormat.date}`}
            showMonthDropdown
            showYearDropdown
            type="woHelperText"
          />
        </div>
      </div>
      <div className={style.whatEnvironmentContainer}>
        <label className={style.fieldLabel}>
          {t('Which environments were these devices exposed to? Check all that apply.')}
        </label>
        <div className={style.whatEnvironmentContainerFields}>
          {environments.map((environment) => (
            <Checkbox
              key={environment.value}
              label={environment.label}
              name={`${WARRANTY_FIELDS.WHAT_ENVIRONMENT}[${environment.value}]`}
              control={control}
              withHelperText={false}
            />
          ))}
        </div>
        <Input
          control={control}
          name={WARRANTY_FIELDS.OTHER_ENVIRONMENT}
          type="woHelperText"
          disabled={!whatEnvironmentsFieldValue[ENVIRONMENT.OTHER]}
        />
      </div>
    </div>
  );
};

export default StepDeviceInformation;
